import React, { useState } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle2 } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [submitting, setSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const onChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error("Please fill in all fields");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/contact", form);
      setSent(true);
      toast.success("Message sent! Srehan will read it soon.");
      setForm({ name: "", email: "", message: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Something went wrong");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="contact-page" className="bg-white">
      <div className="max-w-[1100px] mx-auto px-4 sm:px-8 py-14">
        <motion.h1
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="font-anton uppercase text-6xl sm:text-8xl leading-[0.9]"
        >
          Say <span className="bg-[#FF3B30] text-white px-3">Hi</span><br />
          to Srehan
        </motion.h1>

        <div className="mt-10 grid grid-cols-12 gap-8">
          <motion.form
            onSubmit={submit}
            data-testid="contact-form"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="col-span-12 lg:col-span-8 bg-white border-4 border-black p-6 sm:p-10 brutal-shadow-lg"
          >
            {sent ? (
              <div data-testid="contact-success" className="flex flex-col items-start gap-4">
                <div className="inline-flex items-center gap-2 bg-[#FFD600] border-2 border-black px-3 py-1">
                  <CheckCircle2 size={18} strokeWidth={3} />
                  <span className="font-anton uppercase text-lg">Sent!</span>
                </div>
                <p className="font-outfit text-lg">
                  Your message arrived safely. Srehan will write back when he's done playing.
                </p>
                <button
                  type="button"
                  onClick={() => setSent(false)}
                  data-testid="contact-send-another"
                  className="brutal-btn"
                >
                  Send another
                </button>
              </div>
            ) : (
              <>
                <label className="block mb-5">
                  <span className="font-anton uppercase text-sm tracking-widest mb-2 block">
                    Your Name
                  </span>
                  <input
                    type="text"
                    value={form.name}
                    onChange={onChange("name")}
                    placeholder="Alex the Builder"
                    className="brutal-input"
                    data-testid="contact-name-input"
                    required
                  />
                </label>
                <label className="block mb-5">
                  <span className="font-anton uppercase text-sm tracking-widest mb-2 block">
                    Email
                  </span>
                  <input
                    type="email"
                    value={form.email}
                    onChange={onChange("email")}
                    placeholder="you@email.com"
                    className="brutal-input"
                    data-testid="contact-email-input"
                    required
                  />
                </label>
                <label className="block mb-6">
                  <span className="font-anton uppercase text-sm tracking-widest mb-2 block">
                    Message
                  </span>
                  <textarea
                    value={form.message}
                    onChange={onChange("message")}
                    rows={5}
                    placeholder="What's up?"
                    className="brutal-input resize-none"
                    data-testid="contact-message-input"
                    required
                  />
                </label>

                <button
                  type="submit"
                  disabled={submitting}
                  data-testid="contact-submit-btn"
                  className="brutal-btn disabled:opacity-60"
                >
                  {submitting ? "Sending…" : "Send Message"}
                  <Send size={16} strokeWidth={3} />
                </button>
              </>
            )}
          </motion.form>

          <motion.aside
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="col-span-12 lg:col-span-4 space-y-4"
          >
            <div className="border-4 border-black bg-[#FFD600] p-6 brutal-shadow">
              <div className="font-anton uppercase text-3xl leading-none">
                Got a<br />
                cool brick?
              </div>
              <p className="font-outfit mt-3 text-sm">
                Tell me what to photograph next. Best ideas get featured.
              </p>
            </div>
            <div className="border-4 border-black bg-black text-white p-6">
              <div className="font-anton uppercase text-xs tracking-[0.3em] text-[#FFD600]">
                Studio
              </div>
              <div className="font-outfit mt-2">
                srehandeshmukh.com
              </div>
            </div>
          </motion.aside>
        </div>
      </div>
    </div>
  );
}
